import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Plus, Trash2, X, Save, ExternalLink, Sparkles } from "lucide-react";
import RichTextEditor from "../../components/cms/RichTextEditor";
import ImageUpload from "../../components/cms/ImageUpload";

export default function PagesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("es");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);

  const load = () => api.get("/admin/pages").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/settings")
      .then((r) => setAiEnabled(!!r.data.ai_translate_enabled))
      .catch(() => setAiEnabled(false));
  }, []);

  const blank = () => ({
    slug: "", title_es: "", title_fr: "", subtitle_es: "", subtitle_fr: "",
    body_es: "", body_fr: "",
    meta_description_es: "", meta_description_fr: "", meta_description_en: "", meta_description_ar: "",
    hero_image: "",
    published: true, show_in_menu: false, menu_order: 0,
  });

  const open = (p) => { setEditing({ ...p }); setTab("es"); setError(""); };
  const close = () => { setEditing(null); setError(""); };

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      if (editing.id) {
        const { id, ...body } = editing;
        await api.patch(`/admin/pages/${id}`, body);
      } else {
        await api.post("/admin/pages", editing);
      }
      close();
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta página?")) return;
    await api.delete(`/admin/pages/${id}`);
    load();
  };

  const translateFromOtherLang = async () => {
    // Always translate from Spanish (the canonical source language).
    const target = tab;
    if (target === "es") return;
    const keys = ["title", "subtitle", "body", "meta_description"];
    setTranslating(true);
    setError("");
    try {
      for (const base of keys) {
        const srcText = (editing[`${base}_es`] || "").trim();
        if (!srcText) continue;
        const { data } = await api.post("/admin/translate", { text: srcText, source_lang: "es", target_lang: target });
        setEditing((prev) => ({ ...prev, [`${base}_${target}`]: data.translated }));
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div data-testid="cms-pages">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F2040]">Páginas</h1>
          <p className="text-sm text-gray-600">Editor visual bilingüe — el contenido se publica directamente en la web.</p>
        </div>
        <button onClick={() => open(blank())} data-testid="pages-add" className="bg-[#C8102E] text-white px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-[#A30D25]">
          <Plus className="w-4 h-4" /> Nueva página
        </button>
      </div>

      <div className="bg-white border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
            <tr>
              <th className="text-left px-5 py-3">Título</th>
              <th className="text-left px-5 py-3">Slug</th>
              <th className="text-left px-5 py-3">Actualizado</th>
              <th className="text-right px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-12 text-gray-400">Sin páginas.</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`page-row-${p.slug}`}>
                <td className="px-5 py-3 font-medium text-[#0F2040]">{p.title_es || p.slug}</td>
                <td className="px-5 py-3 text-gray-500 text-xs">
                  <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="hover:text-[#C8102E] inline-flex items-center gap-1">
                    /{p.slug} <ExternalLink className="w-3 h-3" />
                  </a>
                </td>
                <td className="px-5 py-3 text-gray-500 text-xs">{p.updated_at ? new Date(p.updated_at).toLocaleString() : "—"}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => open(p)} className="px-3 py-1.5 text-xs border border-gray-200 hover:border-[#0F2040] mr-2">Editar</button>
                  <button onClick={() => remove(p.id)} className="p-1.5 text-gray-400 hover:text-red-600 inline-block align-middle">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-stretch justify-center" onClick={close}>
          <div className="bg-white max-w-5xl w-full my-4 mx-4 flex flex-col max-h-[calc(100vh-2rem)]" onClick={(e) => e.stopPropagation()} data-testid="page-editor">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h2 className="font-semibold text-[#0F2040]">{editing.id ? "Editar página" : "Nueva página"}</h2>
                {editing.slug && <p className="text-xs text-gray-500 mt-0.5">URL: /{editing.slug}</p>}
              </div>
              <button onClick={close} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Slug *</label>
                  <input value={editing.slug} onChange={(e) => setEditing((prev) => ({ ...prev, slug: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm" placeholder="la-camara" data-testid="page-slug" />
                </div>
                <div>
                  <ImageUpload
                    label="Imagen del hero"
                    aspect="video"
                    value={editing.hero_image}
                    onChange={(v) => setEditing((prev) => ({ ...prev, hero_image: v }))}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-6 py-3 border-y border-gray-100 items-center" data-testid="page-publish-controls">
                <label className="inline-flex items-center gap-2 text-sm text-[#0F2040] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editing.published ?? true}
                    onChange={(e) => setEditing((prev) => ({ ...prev, published: e.target.checked }))}
                    className="accent-[#C8102E]"
                    data-testid="page-published"
                  />
                  Publicado
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-[#0F2040] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editing.show_in_menu ?? false}
                    onChange={(e) => setEditing((prev) => ({ ...prev, show_in_menu: e.target.checked }))}
                    className="accent-[#C8102E]"
                    data-testid="page-show-in-menu"
                  />
                  Mostrar en el menú
                </label>
                <div className="flex items-center gap-2 text-sm">
                  <label className="text-xs uppercase tracking-wider text-gray-500">Orden</label>
                  <input
                    type="number"
                    value={editing.menu_order ?? 0}
                    onChange={(e) => setEditing((prev) => ({ ...prev, menu_order: Number(e.target.value) || 0 }))}
                    className="w-20 px-2 py-1.5 border border-gray-200 text-sm"
                    data-testid="page-menu-order"
                  />
                </div>
              </div>

              <div className="border-b border-gray-200 flex items-center gap-1 flex-wrap" data-testid="page-lang-tabs">
                {[
                  { k: "es", label: "Español" },
                  { k: "fr", label: "Français" },
                  { k: "en", label: "English" },
                  { k: "ar", label: "العربية" },
                ].map((t) => (
                  <button
                    key={t.k}
                    type="button"
                    onClick={() => setTab(t.k)}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      tab === t.k
                        ? "border-[#C8102E] text-[#0F2040]"
                        : "border-transparent text-gray-500 hover:text-[#0F2040]"
                    }`}
                    data-testid={`page-tab-${t.k}`}
                  >
                    {t.label}
                  </button>
                ))}
                {aiEnabled && (
                  <button
                    type="button"
                    onClick={translateFromOtherLang}
                    disabled={translating}
                    title={`Rellenar los campos ${tab.toUpperCase()} traduciendo desde ES con IA`}
                    data-testid="page-ai-translate"
                    className="ml-auto mr-1 mb-1 inline-flex items-center gap-1.5 text-xs text-[#C8102E] border border-[#C8102E]/40 px-3 py-1.5 hover:bg-[#C8102E] hover:text-white transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {translating ? "Traduciendo…" : `Traducir con IA → ${tab.toUpperCase()}`}
                  </button>
                )}
              </div>

              {["es", "fr", "en", "ar"].map((L) => tab === L && (
                <div key={L} className="space-y-4" data-testid={`page-pane-${L}`} dir={L === "ar" ? "rtl" : "ltr"}>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Título ({L.toUpperCase()})</label>
                    <input
                      value={editing[`title_${L}`] ?? ""}
                      onChange={(e) => setEditing((prev) => ({ ...prev, [`title_${L}`]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
                      dir={L === "ar" ? "rtl" : "ltr"}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Subtítulo ({L.toUpperCase()})</label>
                    <input
                      value={editing[`subtitle_${L}`] ?? ""}
                      onChange={(e) => setEditing((prev) => ({ ...prev, [`subtitle_${L}`]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
                      dir={L === "ar" ? "rtl" : "ltr"}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Cuerpo ({L.toUpperCase()})</label>
                    <RichTextEditor
                      testId={`page-body-${L}`}
                      value={editing[`body_${L}`] ?? ""}
                      onChange={(html) => setEditing((prev) => ({ ...prev, [`body_${L}`]: html }))}
                      placeholder="Comience a escribir…"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Meta descripción SEO ({L.toUpperCase()}) · 150-160 caracteres</label>
                    <textarea
                      rows={2}
                      value={editing[`meta_description_${L}`] ?? ""}
                      onChange={(e) => setEditing((prev) => ({ ...prev, [`meta_description_${L}`]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
                      dir={L === "ar" ? "rtl" : "ltr"}
                      placeholder="Descripción que se mostrará en Google. Vacío = descripción global del sitio."
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
              {error ? <p className="text-sm text-red-600">{error}</p> : <span />}
              <div className="flex gap-2">
                <button onClick={close} className="px-4 py-2 text-sm border border-gray-200 hover:bg-gray-50">Cancelar</button>
                <button onClick={save} disabled={saving} data-testid="page-save" className="bg-[#0F2040] text-white px-5 py-2 text-sm flex items-center gap-2 hover:bg-[#1A2F5C] disabled:opacity-50">
                  <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
